import { useRouter } from 'next/router'
import PropTypes from 'prop-types'
import POSTS_QUERY from 'graphql/posts.query'
import { NEWS_POSTS_PER_PAGE } from '@constants'

import Layout from 'components/Layout'
import Loading from 'components/Loading'
import WordpressSEO from 'components/WordpressSEO'
import Pagination from 'components/Pagination'

import s from './archive.module.css'
import StateInitializer from '../../../../components/StateInitializer'
import getGlobalSettings from '../../../../helpers/getGlobalSettings'
import getSites from '../../../../helpers/getSites'
import NewsHeader from '../../../../components/NewsHeader'
import ArticleGrid from '../../../../components/ArticleGrid'
import { createApolloClient } from '../../../../lib/createApolloClient'
import * as Sentry from '@sentry/nextjs'

function Archive({
  globalSettings,
  sites,
  siteSlug,
  categorySlug = '',
  page = '',
  site = '',
  posts = [],
  categoriesMenu = [],
  offsetPagination = {},
}) {
  const router = useRouter()
  if (router.fallback || !page) return <Loading />

  return (
    <StateInitializer
      globalSettings={globalSettings}
      sites={sites}
      siteSlug={siteSlug}
    >
      <Layout globalSettings={globalSettings}>
        <WordpressSEO
          url={`/${site}/news/archive/${page}`}
          title="Archive"
          desc="Latest news from our talent and models at Select Model London. Select Model Management is an iconic global network of boutique model and talent agencies."
          image={posts?.[0]?.acfPost?.featuredImage?.node?.mediaItemUrl}
        />
        <NewsHeader categorySlug={categorySlug} categories={categoriesMenu} />
        {posts?.length > 0 && (
          <ArticleGrid
            className={s.posts}
            articleClassName={s.post}
            articles={posts}
          />
        )}
        <Pagination
          pagination={offsetPagination}
          currentPage={parseInt(page, 10)}
        />
      </Layout>
    </StateInitializer>
  )
}

Archive.propTypes = {
  globalSettings: PropTypes.object.isRequired,
  sites: PropTypes.array.isRequired,
  siteSlug: PropTypes.string.isRequired,
  posts: PropTypes.array,
  categorySlug: PropTypes.string,
  offsetPagination: PropTypes.object,
}

export default Archive

export async function getStaticProps({ params }) {
  const { site, page } = params

  Sentry.addBreadcrumb({
    category: 'getStaticProps',
    message: `Generating static props for News Archive page: page: ${page} for site: ${site}`,
    level: 'info',
    data: { params },
  })

  const apolloClient = createApolloClient()

  const [globalSettings, sites, { data, error }] = await Promise.all([
    getGlobalSettings(site),
    getSites(),
    apolloClient.query({
      query: POSTS_QUERY,
      context: { site },
      variables: {
        withCategoriesMenu: true,
        offset: page ? (parseInt(page, 10) - 1) * NEWS_POSTS_PER_PAGE : null,
      },
    }),
  ])

  if (error) {
    Sentry.captureException(new Error(`Error loading Article props - ${error}`))
    console.error(`Error loading News Archive props - ${error}`)
  }

  return {
    props: {
      ...params,
      categorySlug: null,
      posts: data.posts.nodes,
      categoriesMenu:
        data.newsPage?.nodes?.[0]?.acfNews?.categoriesMenu?.nodes ?? null,
      offsetPagination: data.posts.pageInfo.offsetPagination,
      globalSettings,
      sites,
      siteSlug: site,
    },
    revalidate: 60 * 60, // Revalidate every hour
  }
}

export async function getStaticPaths() {
  const paths = []

  // const sites = await getSitesForStaticPaths(
  //   ({ site }) => ({
  //     query: POSTS_QUERY,
  //     context: { site },
  //     variables: {
  //       featuredInBlocks: false,
  //       limit: 1,
  //     },
  //   }),
  //   data =>
  //     Math.ceil(
  //       data.posts.pageInfo.offsetPagination.total / NEWS_POSTS_PER_PAGE
  //     )
  // )

  // sites.forEach(site => {
  //   for (let i = 0; i < site[1] - 1; i++) {
  //     paths.push({
  //       params: {
  //         site: site[0],
  //         page: (i + 2).toString(),
  //       },
  //     })
  //   }
  // })

  return {
    paths,
    fallback: true,
  }
}
